'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import SearchSection from '@/components/SearchSection';
import Image from 'next/image';
import PremiumHero from '@/components/home/PremiumHero';
import PremiumExperienceCategories from '@/components/home/PremiumExperienceCategories';
import CatalogHero from '@/components/home/CatalogHero';
import PremiumDeck, { type DeckMode } from '@/components/home/PremiumDeck';
import V3MobileNotice from '@/components/home/V3MobileNotice';
import { useMobileBookingsDisabled } from '@/hooks/useMobileBookingsDisabled';
import { useContent } from '@/components/visual-editing/PageContentProvider';
import PremiumTrustBar from '@/components/home/PremiumTrustBar';
import MaritimeFooterWaves from '@/components/MaritimeFooterWaves';
import AnchorSection from '@/components/AnchorSection';
import BoatCard from '@/components/BoatCard';
import { SearchState, Boat } from '@/types';
import { ArrowRight, Anchor, Compass } from 'lucide-react';
import ScrollCueButton from '@/components/home/ScrollCueButton';
import { boatService, Boat as BackendBoat } from '@/services/boatService';
import { getPerDayEquivalent, getUnitPrice, type RentalPriceSource } from '@/lib/rental-pricing';
import { bookableCapacity } from '@/lib/capacity';
import { checkBatchAvailability } from '@/services/bookingAvailabilityService';
import CatalogToolbar, { ViewMode, SortOption } from '@/components/catalog/CatalogToolbar';
import { useFavorites } from '@/hooks/useFavorites';
import CatalogFilters, { CatalogFiltersState } from '@/components/catalog/CatalogFilters';
import CatalogWhereInput from '@/components/catalog/CatalogWhereInput';
import ActiveFilterChips from '@/components/catalog/ActiveFilterChips';
import { CompareProvider } from '@/components/catalog/CompareContext';
import CompareBar from '@/components/catalog/CompareBar';
import CompareModal from '@/components/catalog/CompareModal';
import BoatListCard from '@/components/catalog/BoatListCard';
import UnavailableBoatModal from '@/components/catalog/UnavailableBoatModal';
import Pagination from '@/components/ui/Pagination';
import { useAuth } from '@/app/providers';
import { useDesignVariants } from '@/components/design/DesignVariantsProvider';
import { t } from '@/locales';
import { useExcursionsEnabled } from '@/hooks/useExcursionsEnabled';

// Dynamic import for map (SSR)
const BoatMap = dynamic(() => import('@/components/catalog/BoatMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full bg-slate-100 rounded-2xl flex items-center justify-center" style={{ minHeight: '500px' }}>
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 font-medium">{t('home.loadingMap')}</p>
      </div>
    </div>
  ),
});

// Mappa tipi barca backend -> frontend
const BOAT_TYPE_MAP: Record<string, Boat['type']> = {
  'yacht': 'Yacht',
  'sailboat': 'Vela',
  'motorboat': 'Motoscafo',
  'catamaran': 'Catamarano',
  'dinghy': 'Gommone',
  'fishing': 'Motoscafo',
  'houseboat': 'Yacht',
  'jetski': 'Moto d\'Acqua',
  'other': 'Motoscafo',
};

// Trasforma amenities backend -> frontend
const transformAmenities = (amenities: any[]): any[] => {
  if (!amenities || !Array.isArray(amenities)) return [];
  return amenities.map(a => ({
    id: a.id,
    key: a.key,
    label: a.label,
    icon: a.icon,
    category: a.category,
    description: a.description,
  }));
};

// Trasforma extras backend -> frontend
const transformExtras = (extras: any[]): any[] => {
  if (!extras || !Array.isArray(extras)) return [];
  return extras.map(e => ({
    id: e.id,
    key: e.key,
    label: e.label,
    description: e.description,
    price: e.BoatExtra?.custom_price ?? e.default_price ?? 0,
    priceType: e.price_type || 'per_day',
  }));
};

// Trasforma barca backend -> frontend
const transformBackendBoat = (boat: BackendBoat): Boat => {
  const coverIndex = boat.cover_image_index || 0;
  const images = boat.image_urls || [];
  const coverImage = images[coverIndex] || images[0] || '';

  return {
    id: boat.id,
    name: boat.name,
    type: boat.boat_type || 'other',
    // Rate per-giorno equivalente (weekly→/7); BoatCard ×7 ricostruisce la tariffa
    // di periodo. Robusto anche con price_per_day NULL (usa price_per_week).
    price: getPerDayEquivalent(boat as unknown as RentalPriceSource) ?? boat.price_per_day ?? 0,
    unitPrice: getUnitPrice(boat as unknown as RentalPriceSource) ?? undefined,
    length: boat.length_meters || 0,
    // Posti prenotabili (ospiti): max_guests ?? max_capacity.
    capacity: bookableCapacity({ max_guests: boat.max_guests, max_capacity: boat.max_capacity })
      ?? 6,
    maxCapacity: boat.max_capacity ?? undefined,
    location: boat.location || boat.city || 'Sardegna',
    image: coverImage,
    gallery: images,
    description: boat.description || '',
    amenities: transformAmenities((boat as any).amenities),
    rating: Number(boat.rating) || 0,
    reviews: boat.reviews_count || 0,
    featured: (boat as any).is_featured === true,
    lastMinuteEnabled: (boat as any).last_minute_enabled === true,
    lastMinuteDaysBefore: (boat as any).last_minute_days_before ?? null,
    lastMinuteDiscountPercent: (boat as any).last_minute_discount_percent ?? null,
    status: boat.status === 'ACTIVE' ? 'Available' : 'Maintenance',
    licenseRequired: boat.requires_license !== false,
    engine: boat.engine_power_hp ? `${boat.engine_power_hp} CV` : undefined,
    year: boat.year_built,
    // Location fields
    city: boat.city || undefined,
    zone: boat.zone || undefined,
    port: boat.port_name || boat.marina_address || undefined,
    port_id: boat.port_id ?? undefined,
    pier: boat.pier || undefined,
    latitude: boat.latitude ? parseFloat(String(boat.latitude)) : undefined,
    longitude: boat.longitude ? parseFloat(String(boat.longitude)) : undefined,
    // Other fields
    enginePower: boat.engine_power_hp,
    skipperAvailable: boat.skipper_available,
    skipperRequired: boat.skipper_required,
    hostessAvailable: boat.hostess_available,
    coverImageIndex: coverIndex,
    rentalMode: (boat as any).rental_mode || 'daily',
    minRentalDays: (boat as any).min_rental_days,
    maxRentalDays: (boat as any).max_rental_days,
    changeoverDay: (boat as any).changeover_day,
    changeoverCheckinTime: (boat as any).changeover_checkin_time,
    changeoverCheckoutTime: (boat as any).changeover_checkout_time,
    shortStaySurcharge: (boat as any).short_stay_surcharge ? parseFloat(String((boat as any).short_stay_surcharge)) : undefined,
    extras: transformExtras((boat as any).extras),
    // Range pricing dal backend (se search ha date)
    priceForRangeTotal: (boat as any).price_for_range_total ?? undefined,
    priceForRangeAvg: (boat as any).price_for_range_avg ?? undefined,
    priceForRangeDays: (boat as any).price_for_range_days ?? undefined,
  };
};

const ITEMS_PER_PAGE = 9;

const initialFilters: CatalogFiltersState = {
  boatTypes: [],
  priceMin: null,
  priceMax: null,
  capacityMin: 2,
  capacityMax: 20,
  port: null,
  zone: null,
  port_id: null,
  where_label: null,
  noLicenseOnly: false,
  skipperAvailable: false,
  onlyAvailableInRange: true,
  lengthMin: null,
  lengthMax: null,
  cabinsMin: 0,
  bathroomsMin: 0,
  yearMin: null,
  crewedOnly: false,
  hostessAvailable: false,
  instantBookingOnly: false,
  freeCancellation: false,
  petFriendly: false,
  hasPromo: false,
  ratingMin: 0,
  manufacturers: [],
  amenities: [],
};

interface RentalHoursSettings {
  opening: string;
  closing: string;
  halfDayCutoff: string;
}

interface HomeCatalogClientProps {
  mode?: 'home' | 'catalog';
  /** Hero settings pre-fetched server-side (evita flash hero vuoto). */
  initialHero?: { urls: string[]; mobileUrls: string[]; rotateMs: number };
  /** Barche pre-fetchate server-side (SSR catalogo → SEO). Idratazione poi
   *  aggiorna via boatService. Forma backend (BackendBoat), trasformata al mount. */
  initialBackendBoats?: BackendBoat[];
}

export default function HomeCatalogClient({ mode = 'home', initialHero, initialBackendBoats }: HomeCatalogClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const design = useDesignVariants();
  const isCatalogMode = mode === 'catalog';
  // Contenuti editabili hero (visual editing). DB additivo: fallback = testo attuale.
  const heroBadge = useContent('hero.badge', 'Sardegna · Mediterraneo');
  const heroTitle = useContent('hero.title', '');
  const heroSubtitle = useContent('hero.subtitle', 'Noleggi, escursioni ed esperienze autentiche per vivere il mare della Sardegna.');
  const heroCtaRentals = useContent('hero.ctaRentals', 'Prenota la tua barca');
  const heroCtaExperiences = useContent('hero.ctaExperiences', 'Esplora esperienze');
  const searchSectionTitle = useContent('searchSection.title', 'Trova la tua barca');
  const searchSectionSubtitle = useContent(
    'searchSection.subtitle',
    'Seleziona porto, date e passeggeri per scoprire le imbarcazioni disponibili.',
  );
  // Home V3 unica (rilasciata a tutti; rimossa la home legacy + beta-gate).
  // V3 deck: default 'C' (scroll-snap nativo). Override via ?deck=a|b per test.
  const [deckMode, setDeckMode] = useState<DeckMode>('C');
  useEffect(() => {
    const q = (searchParams.get('deck') || '').toUpperCase();
    if (q === 'A' || q === 'B' || q === 'C') setDeckMode(q as DeckMode);
  }, [searchParams]);
  // V3 è ottimizzato desktop: su tablet/mobile (<1024px) mostriamo una notice.
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  // Landing "Stiamo costruendo l'app": mostrata sul mobile SOLO in modalità blocco
  // (mobile_bookings_disabled). Se il mobile arriva qui col blocco ON, il redirect
  // a m.* era OFF (altrimenti il proxy l'avrebbe già portato via) → landing corretta.
  const { disabled: mobileBlocked } = useMobileBookingsDisabled();
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);


  // Init searchFilters: in mode='catalog' leggi query params
  const initialSearchFilters: SearchState | null = useMemo(() => {
    if (!isCatalogMode) return null;
    const city = searchParams.get('city') || '';
    const startDate = searchParams.get('from') || '';
    const endDate = searchParams.get('to') || '';
    const passengers = parseInt(searchParams.get('passengers') || '0', 10);
    const boatType = (searchParams.get('boatType') as SearchState['boatType']) || 'Tutte';
    const noLicenseOnly = searchParams.get('noLicense') === '1';
    if (!city && !startDate && !endDate && !passengers && boatType === 'Tutte' && !noLicenseOnly) return null;
    return {
      location: city,
      startDate,
      startTime: '09:00',
      endDate,
      endTime: '18:00',
      passengers: passengers || 2,
      boatType,
      noLicenseOnly,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCatalogMode]);

  const [searchFilters, setSearchFilters] = useState<SearchState | null>(initialSearchFilters);
  const [boatCombinations, setBoatCombinations] = useState<any[]>([]);
  const [combosLoading, setCombosLoading] = useState(false);
  const [realBoats, setRealBoats] = useState<Boat[]>(() => (initialBackendBoats ?? []).map(transformBackendBoat));
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [rentalHours, setRentalHours] = useState<RentalHoursSettings>({
    opening: '09:00',
    closing: '18:00',
    halfDayCutoff: '13:00',
  });
  const [loading, setLoading] = useState(!(initialBackendBoats && initialBackendBoats.length > 0));
  const { isFavorite, toggleFavorite } = useFavorites();
  const { enabled: excursionsEnabled } = useExcursionsEnabled();

  // Redirect automatico per admin e vendor
  useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'ADMIN') {
        router.replace('/admin');
      } else if (user.role === 'VENDOR' || user.role === 'COMPANY') {
        router.replace('/partner');
      }
    }
  }, [user, authLoading, router]);

  // Catalog state
  const [catalogFilters, setCatalogFilters] = useState<CatalogFiltersState>(initialFilters);
  const [sortBy, setSortBy] = useState<SortOption>('price_asc');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  // Hover su card in split view → highlight pin corrispondente in mappa.
  const [hoveredBoatId, setHoveredBoatId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Availability state
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, boolean>>({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Unavailable boat modal
  const [unavailableModalBoat, setUnavailableModalBoat] = useState<Boat | null>(null);

  // Carica barche, città e impostazioni dal backend.
  // Re-fetch quando cambia il range date di ricerca: il backend filtra le barche
  // con CLOSED/NOT_AVAILABLE / bookings overlap su quel range.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const boatFilters: { status: string; from?: string; to?: string; startDate?: string; endDate?: string } = { status: 'ACTIVE' };
        // Filtro disponibilita': si applica solo se l'utente ha sia date sia toggle attivo.
        if (
          searchFilters?.startDate &&
          searchFilters?.endDate &&
          catalogFilters.onlyAvailableInRange
        ) {
          boatFilters.from = searchFilters.startDate;
          boatFilters.to = searchFilters.endDate;
        }
        // Range pricing: SOLO con ricerca (totale reale del periodo scelto).
        // SENZA ricerca il catalogo mostra "a partire da" = prezzo base markup-ato
        // (stabile, minimo, coerente col detail). Niente media mese né tariffa-di-oggi
        // che gonfiavano/variavano il "da €" (es. Molinari €225 invece di €170).
        if (searchFilters?.startDate && searchFilters?.endDate) {
          boatFilters.startDate = searchFilters.startDate;
          boatFilters.endDate = searchFilters.endDate;
        }
        // Endpoint dedicato pubblico per rental_hours: utenti anonimi vedono
        // catalog senza autenticarsi, quindi getAllSettings (admin-only) non
        // va usato qui — generava 401 in console.
        const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';
        const [backendBoats, cities, rentalHoursRes] = await Promise.all([
          boatService.getBoats(boatFilters),
          boatService.getAvailableCities().catch(() => []),
          fetch(`${apiBase}/settings/public/rental-hours`).then(r => r.json()).catch(() => null),
        ]);
        const transformed = backendBoats.map(transformBackendBoat);
        setRealBoats(transformed);
        setAvailableCities(cities);

        if (rentalHoursRes?.success && rentalHoursRes?.data) {
          setRentalHours({
            opening: rentalHoursRes.data.opening || '09:00',
            closing: rentalHoursRes.data.closing || '18:00',
            halfDayCutoff: rentalHoursRes.data.halfDayCutoff || '13:00',
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchFilters?.startDate, searchFilters?.endDate, catalogFilters.onlyAvailableInRange]);

  // Check batch availability when search dates change
  useEffect(() => {
    if (!searchFilters?.startDate || !searchFilters?.endDate || realBoats.length === 0) {
      setAvailabilityMap({});
      return;
    }

    const boatIds = realBoats.map(b => b.id);
    setAvailabilityLoading(true);

    checkBatchAvailability(searchFilters.startDate, searchFilters.endDate, boatIds)
      .then(data => {
        setAvailabilityMap(data.availability || {});
      })
      .catch(err => {
        console.error('Batch availability check failed:', err);
        setAvailabilityMap({});
      })
      .finally(() => setAvailabilityLoading(false));
  }, [searchFilters?.startDate, searchFilters?.endDate, realBoats]);

  // Usa solo barche reali dal backend
  const fleet = useMemo(() => realBoats, [realBoats]);

  // Get available ports for filter
  const availablePorts = useMemo(() => {
    const ports = new Set<string>();
    fleet.forEach(boat => {
      if (boat.port) ports.add(boat.port);
    });
    return Array.from(ports).sort();
  }, [fleet]);

  // Manufacturers presenti in fleet (per filter brand multi-select)
  const availableManufacturers = useMemo(() => {
    const set = new Set<string>();
    fleet.forEach(b => { if (b.manufacturer) set.add(b.manufacturer); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
  }, [fleet]);

  // Amenities presenti in fleet (chiave + label) — distinti per non duplicare
  const availableAmenities = useMemo(() => {
    const map = new Map<string, string>();
    fleet.forEach(b => {
      const ams = (b.amenities || []) as Array<unknown>;
      ams.forEach(a => {
        const key = typeof a === 'string' ? a : (a as { key?: string })?.key;
        const label = typeof a === 'string' ? a : (a as { label?: string })?.label || (a as { key?: string })?.key;
        if (key && label) map.set(String(key).toLowerCase(), String(label));
      });
    });
    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it'));
  }, [fleet]);

  // Get price range for filter
  const priceRange = useMemo(() => {
    if (fleet.length === 0) return { min: 0, max: 1000 };
    const prices = fleet.map(b => b.price);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }, [fleet]);

  // Apply all filters, search, and sorting
  const filteredAndSortedBoats = useMemo(() => {
    let result = [...fleet];

    // Apply search filters from SearchSection
    if (searchFilters) {
      result = result.filter(boat => {
        if (searchFilters.noLicenseOnly && boat.licenseRequired) return false;
        // Guard: salta il check se capacity manca (0/NaN/undefined non devono
        // escludere la barca a fronte di un default passengers=2).
        if (typeof boat.capacity === 'number' && boat.capacity > 0 && boat.capacity < searchFilters.passengers) return false;
        // searchFilters.location ora contiene il PORTO reale selezionato nel combo
        // (es. "Marina di Portisco"). Confronta contro il porto della barca
        // (port_name/marina_address) con fallback su location/city per le barche
        // senza porto valorizzato.
        if (searchFilters.location) {
          const target = searchFilters.location.toLowerCase();
          const haystack = `${boat.port || ''} ${boat.location || ''} ${boat.city || ''}`.toLowerCase();
          if (!haystack.includes(target)) return false;
        }
        // Allinea la search bar al catalog filter: se l'utente ha scelto un
        // boatType nella SearchSection, applicalo (era ignorato → l'utente
        // poteva selezionare "Gommone" in alto e vedere comunque tutto).
        if (searchFilters.boatType && searchFilters.boatType !== 'Tutte' && boat.type !== searchFilters.boatType) return false;
        return true;
      });
    }

    // Apply catalog filters
    if (catalogFilters.boatTypes.length > 0) {
      result = result.filter(boat => catalogFilters.boatTypes.includes(boat.type));
    }

    if (catalogFilters.priceMin !== null) {
      result = result.filter(boat => boat.price >= catalogFilters.priceMin!);
    }

    if (catalogFilters.priceMax !== null) {
      result = result.filter(boat => boat.price <= catalogFilters.priceMax!);
    }

    if (catalogFilters.capacityMin > 2) {
      result = result.filter(boat => boat.capacity >= catalogFilters.capacityMin);
    }

    if (catalogFilters.capacityMax < 20) {
      result = result.filter(boat => boat.capacity <= catalogFilters.capacityMax);
    }

    // Filtri luogo: porto specifico (FK) → zona → città. Matching esatto.
    if (catalogFilters.port_id) {
      result = result.filter(
        (boat) => (boat as unknown as { port_id?: string | null }).port_id === catalogFilters.port_id,
      );
    }
    if (catalogFilters.zone) {
      const zTarget = catalogFilters.zone.trim().toLowerCase();
      result = result.filter((boat) => (boat.zone || '').trim().toLowerCase() === zTarget);
    }
    if (catalogFilters.port) {
      // 'port' nel filtro rappresenta la città (compat. storica con il campo port).
      const cTarget = catalogFilters.port.trim().toLowerCase();
      result = result.filter((boat) => (boat.city || '').trim().toLowerCase() === cTarget);
    }

    if (catalogFilters.noLicenseOnly) {
      result = result.filter(boat => !boat.licenseRequired);
    }

    if (catalogFilters.skipperAvailable) {
      result = result.filter(boat => boat.skipperAvailable);
    }

    if (catalogFilters.crewedOnly) {
      result = result.filter(boat => boat.skipperRequired);
    }

    if (catalogFilters.hostessAvailable) {
      result = result.filter(boat => boat.hostessAvailable);
    }

    if (catalogFilters.instantBookingOnly) {
      // boat.auto_confirm_bookings espone l'auto-confirm vendor (default true se non valorizzato)
      result = result.filter(boat => (boat as unknown as { auto_confirm_bookings?: boolean }).auto_confirm_bookings !== false);
    }

    if (catalogFilters.lengthMin != null) {
      result = result.filter(boat => (boat.length || 0) >= catalogFilters.lengthMin!);
    }
    if (catalogFilters.lengthMax != null) {
      result = result.filter(boat => (boat.length || 0) <= catalogFilters.lengthMax!);
    }

    if ((catalogFilters.cabinsMin ?? 0) > 0) {
      result = result.filter(boat => (boat.cabins || 0) >= catalogFilters.cabinsMin!);
    }

    if ((catalogFilters.bathroomsMin ?? 0) > 0) {
      result = result.filter(boat => (boat.bathrooms || 0) >= catalogFilters.bathroomsMin!);
    }

    if (catalogFilters.yearMin != null) {
      result = result.filter(boat => (boat.year || 0) >= catalogFilters.yearMin!);
    }

    if ((catalogFilters.ratingMin ?? 0) > 0) {
      result = result.filter(boat => boat.rating >= (catalogFilters.ratingMin ?? 0));
    }

    if (catalogFilters.freeCancellation) {
      result = result.filter(boat => {
        const policy = (boat as unknown as { cancellation_policy?: string }).cancellation_policy || '';
        return /free|gratuita|flexible|flessibile/i.test(policy);
      });
    }

    if (catalogFilters.petFriendly) {
      result = result.filter(boat => {
        const ams = (boat.amenities || []) as Array<unknown>;
        return ams.some((a) => {
          const label = typeof a === 'string' ? a : (a as { label?: string; key?: string })?.label || (a as { key?: string })?.key || '';
          return /pet|animal/i.test(String(label));
        });
      });
    }

    if (catalogFilters.hasPromo) {
      result = result.filter(boat => !!(boat as unknown as { hasPromo?: boolean; promo_active?: boolean }).hasPromo
        || !!(boat as unknown as { promo_active?: boolean }).promo_active);
    }

    if (catalogFilters.manufacturers && catalogFilters.manufacturers.length > 0) {
      const set = new Set(catalogFilters.manufacturers.map(m => m.toLowerCase()));
      result = result.filter(boat => boat.manufacturer && set.has(boat.manufacturer.toLowerCase()));
    }

    if (catalogFilters.amenities && catalogFilters.amenities.length > 0) {
      const want = new Set(catalogFilters.amenities.map(s => s.toLowerCase()));
      result = result.filter(boat => {
        const ams = (boat.amenities || []) as Array<unknown>;
        const have = new Set<string>();
        for (const a of ams) {
          const k = typeof a === 'string' ? a : (a as { key?: string })?.key || (a as { label?: string })?.label;
          if (k) have.add(String(k).toLowerCase());
        }
        for (const id of want) if (!have.has(id)) return false;
        return true;
      });
    }

    // Apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(boat =>
        boat.name.toLowerCase().includes(query) ||
        (boat.description?.toLowerCase().includes(query)) ||
        (boat.port?.toLowerCase().includes(query)) ||
        boat.location.toLowerCase().includes(query) ||
        boat.type.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'reviews':
          return b.reviews - a.reviews;
        case 'newest':
          return (b.year || 0) - (a.year || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    // When dates are selected, push unavailable boats to the end
    if (searchFilters?.startDate && searchFilters?.endDate && Object.keys(availabilityMap).length > 0) {
      const available = result.filter(b => availabilityMap[b.id] !== false);
      const unavailable = result.filter(b => availabilityMap[b.id] === false);
      result = [...available, ...unavailable];
    }

    return result;
  }, [fleet, searchFilters, catalogFilters, searchQuery, sortBy, availabilityMap]);

  // Suggerisce coppie di barche se la richiesta passeggeri supera la capacità
  // massima singola DISPONIBILE NELL'AREA E TIPO selezionati. Il pool deve
  // rispettare gli stessi filtri della ricerca principale (almeno location +
  // tipo), altrimenti basterebbe una catamaran a Olbia per disattivare il
  // suggerimento di combinazioni quando si cerca ad Alghero.
  useEffect(() => {
    if (!searchFilters?.passengers || searchFilters.passengers < 2) { setBoatCombinations([]); return; }
    const typeFromSearch = searchFilters.boatType && searchFilters.boatType !== 'Tutte' ? searchFilters.boatType : null;
    const typeFromSidebar = catalogFilters.boatTypes.length === 1 ? catalogFilters.boatTypes[0] : null;
    const typeFilter = typeFromSearch || typeFromSidebar;
    const locationFilter = searchFilters.location?.trim().toLowerCase() || '';

    let pool = realBoats;
    if (typeFilter) pool = pool.filter(b => b.type === typeFilter);
    // locationFilter è ora il PORTO reale: matcha su port/location/city.
    if (locationFilter) {
      pool = pool.filter(b =>
        `${b.port || ''} ${b.location || ''} ${b.city || ''}`.toLowerCase().includes(locationFilter)
      );
    }

    const maxSingle = pool.reduce((m, b) => Math.max(m, b.capacity || 0), 0);
    // Se nel pool (area + tipo) c'è già una barca singola sufficiente, no combo.
    if (maxSingle >= searchFilters.passengers) { setBoatCombinations([]); return; }

    setCombosLoading(true);
    const params = new URLSearchParams({ passengers: String(searchFilters.passengers) });
    if (searchFilters.startDate) params.set('startDate', searchFilters.startDate);
    if (searchFilters.endDate) params.set('endDate', searchFilters.endDate);
    if (typeFilter) params.set('boatType', typeFilter);
    // Backend usa `city` per matching esatto su boats.city
    if (locationFilter) params.set('city', searchFilters.location);
    fetch(`/api/boats/search-combinations?${params.toString()}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(data => setBoatCombinations(data?.success ? (data.data || []) : []))
      .catch(() => setBoatCombinations([]))
      .finally(() => setCombosLoading(false));
  }, [searchFilters?.passengers, searchFilters?.startDate, searchFilters?.endDate, searchFilters?.boatType, searchFilters?.location, catalogFilters.boatTypes, realBoats]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedBoats.length / ITEMS_PER_PAGE);
  const paginatedBoats = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedBoats.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedBoats, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [catalogFilters, searchQuery, sortBy, searchFilters]);

  const handleSearch = (filters: SearchState) => {
    // Home (premium o legacy): la ricerca porta a /barche col MARKETPLACE booking
    // (catalogo filtrato) coi query params. La home resta hero + ricerca, niente
    // catalogo inline. /barche senza param resta vetrina flotta esplorativa.
    if (!isCatalogMode) {
      const qs = new URLSearchParams();
      if (filters.location) qs.set('city', filters.location);
      if (filters.startDate) qs.set('from', filters.startDate);
      if (filters.endDate) qs.set('to', filters.endDate);
      if (filters.passengers && filters.passengers !== 2) qs.set('passengers', String(filters.passengers));
      if (filters.boatType && filters.boatType !== 'Tutte') qs.set('boatType', filters.boatType);
      if (filters.noLicenseOnly) qs.set('noLicense', '1');
      // SearchSection richiede sempre una startDate → `from` è garantito,
      // quindi /barche rende il marketplace (non la vetrina flotta).
      router.push(`/barche?${qs.toString()}`);
      return;
    }
    // /marketplace (mode=catalog) / home legacy: catalog inline, scroll
    setSearchFilters(filters);
    setTimeout(() => {
      const catalog = document.getElementById('catalog');
      if (catalog) catalog.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleResetAll = () => {
    setSearchFilters(null);
    setCatalogFilters(initialFilters);
    setSearchQuery('');
    setSortBy('price_asc');
    setCurrentPage(1);
    setAvailabilityMap({});
  };

  const handleClickUnavailable = useCallback((boat: Boat) => {
    setUnavailableModalBoat(boat);
  }, []);

  const handleSelectAlternativeDates = useCallback((newStart: string, newEnd: string) => {
    setSearchFilters(prev => prev ? { ...prev, startDate: newStart, endDate: newEnd } : prev);
    setUnavailableModalBoat(null);
  }, []);

  // Get similar available boats for the unavailable modal
  const similarAvailableBoats = useMemo(() => {
    if (!unavailableModalBoat) return [];
    return fleet
      .filter(b => b.id !== unavailableModalBoat.id && availabilityMap[b.id] !== false)
      .map(b => {
        let score = 0;
        if (b.type === unavailableModalBoat.type) score += 30;
        if (Math.abs(b.price - unavailableModalBoat.price) / unavailableModalBoat.price < 0.3) score += 20;
        if (b.location === unavailableModalBoat.location) score += 15;
        if (Math.abs(b.capacity - unavailableModalBoat.capacity) <= 2) score += 10;
        return { boat: b, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(item => item.boat);
  }, [unavailableModalBoat, fleet, availabilityMap]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const hasActiveFilters = searchFilters !== null ||
    catalogFilters.boatTypes.length > 0 ||
    catalogFilters.priceMin !== null ||
    catalogFilters.priceMax !== null ||
    catalogFilters.capacityMin > 2 ||
    catalogFilters.capacityMax < 20 ||
    catalogFilters.port !== null ||
    catalogFilters.zone !== null ||
    catalogFilters.port_id !== null ||
    catalogFilters.noLicenseOnly ||
    catalogFilters.skipperAvailable ||
    searchQuery.trim() !== '';

  // Landing mobile "app in arrivo" (blocco ON, redirect OFF): rendi SOLO la landing,
  // NON la home dietro. Se la home desktop resta nel DOM sotto l'overlay, trabocca in
  // orizzontale su alcuni Android → il browser zooma-out la viewport e la landing
  // (fixed) si rimpicciolisce. Renderizzando solo la landing niente overflow, resa
  // identica su tutti i device. (Gate redirect già gestito dal proxy prima di qui.)
  if (!isCatalogMode && isDesktop === false && mobileBlocked) {
    return <V3MobileNotice />;
  }

  return (
    <CompareProvider>
    <div data-cid="home-page">
      <CompareBar />
      <CompareModal />
      {/* ───────── Hero — solo in mode='home' (desktop) ───────── */}
      {!isCatalogMode && isDesktop === true && (
        <div className="relative h-[100svh]">
          {/* Hero fisso dietro: slideshow rotante (decorative, no content) */}
          <div className="absolute inset-0 z-0" data-cid="premium-hero-fixed-bg">
            <PremiumHero decorative initialImages={initialHero?.urls} initialRotateMs={initialHero?.rotateMs} />
          </div>

          <PremiumDeck
            mode={deckMode}
            slides={[
              {
                id: 'top',
                label: 'Home',
                content: (
                  <div className="h-full flex items-center pt-20 pb-32" data-cid="premium-top-overlay">
                    <div className="relative w-full max-w-6xl mx-auto px-6 md:px-12 py-20">
                      <div className="max-w-3xl">
                        <span data-cid="premium-hero-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-[11px] font-bold uppercase tracking-[0.18em] mb-8">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                          {heroBadge}
                        </span>
                        <h1 data-cid="premium-hero-title" className="font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6 drop-shadow-2xl">
                          {heroTitle
                            ? heroTitle.split('\n').map((line, i, arr) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < arr.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            : (
                              <>
                                Che esperienza vuoi vivere<br />
                                in barca <em className="font-serif italic font-medium text-cyan-200/95">oggi</em> in Sardegna?
                              </>
                            )}
                        </h1>
                        <p data-cid="premium-hero-subtitle" className="text-white/85 text-base md:text-lg lg:text-xl font-light leading-relaxed max-w-2xl mb-10 drop-shadow-lg">
                          {heroSubtitle}
                        </p>
                        <div data-cid="premium-hero-cta-group" className="flex flex-col sm:flex-row items-center sm:items-stretch gap-3 sm:gap-4">
                          <button
                            type="button"
                            data-cid="premium-hero-cta-rentals"
                            onClick={() => window.dispatchEvent(new CustomEvent('deck:goto', { detail: 'ricerca' }))}
                            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-slate-900 font-bold text-sm uppercase tracking-wider hover:bg-cyan-50 transition-all shadow-2xl shadow-black/30 active:scale-[0.98]"
                          >
                            <Anchor size={16} className="text-cyan-700" />
                            {heroCtaRentals}
                            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                          </button>
                          <span aria-hidden="true" data-cid="premium-hero-cta-divider" className="hidden sm:block w-px h-14 self-center bg-gradient-to-b from-transparent via-white/60 to-transparent" />
                          <span aria-hidden="true" data-cid="premium-hero-cta-divider-mobile" className="sm:hidden h-px w-14 self-center bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                          <button
                            type="button"
                            data-cid="premium-hero-cta-experiences"
                            onClick={() => window.dispatchEvent(new CustomEvent('deck:goto', { detail: 'esperienze' }))}
                            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/30 font-bold text-sm uppercase tracking-wider hover:bg-white/20 transition-all active:scale-[0.98]"
                          >
                            <Compass size={16} className="text-emerald-300" />
                            {heroCtaExperiences}
                            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: 'esperienze',
                label: 'Esperienze',
                content: (
                  <AnchorSection variant="light" transparent>
                    <PremiumExperienceCategories />
                  </AnchorSection>
                ),
              },
              {
                id: 'ricerca',
                label: 'Cerca',
                content: (
                  <CatalogHero transparent id={null} bottomCue={<ScrollCueButton targetId="footer" label="Contatti" tone="dark" />}>
                    <div className="text-center mb-6" data-cid="home-search-section-header">
                      <h2 data-cid="home-search-section-title" className="text-2xl md:text-3xl font-display font-bold text-white drop-shadow-lg">
                        {searchSectionTitle}
                      </h2>
                      <p data-cid="home-search-section-subtitle" className="text-sm md:text-base text-white/80 mt-1 font-medium">
                        {searchSectionSubtitle}
                      </p>
                    </div>
                    <SearchSection onSearch={handleSearch} availableCities={availableCities} availablePorts={availablePorts} rentalHours={rentalHours} />
                  </CatalogHero>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Section catalog: SOLO su /marketplace (mode='catalog'). La home V3 resta
          hero + deck; il catalogo booking è su /barche. */}
      {isCatalogMode && (
      <section
        id="catalog"
        className={`relative overflow-hidden pt-4 pb-24 scroll-mt-20 ${
          design.catalog_bg === 'flat'
            ? 'bg-slate-100'
            : 'bg-gradient-to-b from-white via-slate-50 to-white'
        }`}
        data-cid="catalog-section"
      >
        {isCatalogMode && design.catalog_bg === 'noise' && (
          <div
            aria-hidden
            className="fixed inset-0 pointer-events-none z-0"
            style={{
              opacity: 0.12,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2' stitchTiles='stitch' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6" data-cid="catalog-container">
        {/* Header */}
        <div className={`flex flex-col md:flex-row items-end justify-between mb-10 gap-6 ${isCatalogMode ? 'pt-24' : ''}`} data-cid="catalog-header">
          <div className="text-center md:text-left max-w-2xl" data-cid="catalog-header-title">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
              Catalogo
            </p>
            <h1 data-cid="catalog-classic-title" className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] text-slate-950">
              {hasActiveFilters ? t('home.searchResults') : t('home.catalogTitle')}
            </h1>
            <p className="text-slate-500 mt-3 leading-relaxed">
              {hasActiveFilters
                ? t('home.searchResultsSubtitle').replace('{count}', String(filteredAndSortedBoats.length))
                : t('home.catalogSubtitle')}
            </p>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleResetAll}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-full text-xs font-semibold hover:bg-slate-950 hover:text-white hover:border-slate-950 transition-colors active:translate-y-px"
            >
              {t('home.resetFilters')}
            </button>
          )}
        </div>

        {/* Barra "Dove?" — autocomplete porto/città/zona */}
        <div className="mb-4" data-cid="catalog-where-bar">
          <CatalogWhereInput
            value={{
              port: catalogFilters.port,
              zone: catalogFilters.zone,
              port_id: catalogFilters.port_id,
              where_label: catalogFilters.where_label,
            }}
            onChange={(patch) => setCatalogFilters((f) => ({ ...f, ...patch }))}
          />
        </div>

        {/* Toolbar */}
        <div className="mb-6" data-cid="boats-catalog-toolbar-wrap">
          <CatalogToolbar
            totalResults={filteredAndSortedBoats.length}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onToggleFilters={() => setMobileFiltersOpen(true)}
            showFiltersButton={true}
            availableOnly={catalogFilters.onlyAvailableInRange}
            onAvailableOnlyChange={(next) => setCatalogFilters(f => ({ ...f, onlyAvailableInRange: next }))}
            hasDateRange={!!(searchFilters?.startDate && searchFilters?.endDate)}
            onRequestDates={() => {
              // Scroll alla SearchSection hero e simula click sul bottone date
              const dateBtn = document.querySelector('[data-cid="search-dates-btn"]') as HTMLElement | null;
              if (dateBtn) {
                dateBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => dateBtn.click(), 400);
              }
            }}
          />
        </div>

        {/* Active filter chips — un click rimuove il filtro corrispondente */}
        <ActiveFilterChips
          filters={catalogFilters}
          onChange={setCatalogFilters}
          onReset={() => setCatalogFilters(initialFilters)}
          amenityLabels={availableAmenities}
          className="mb-6"
        />

        {/* Main Content */}
        <div className="flex gap-8" data-cid="catalog-main">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0" data-cid="catalog-filters-aside">
            <div className="sticky top-24" data-cid="catalog-filters-sticky">
              <CatalogFilters
                filters={catalogFilters}
                onFiltersChange={setCatalogFilters}
                availableCities={availableCities}
                availablePorts={availablePorts}
                availableManufacturers={availableManufacturers}
                availableAmenities={availableAmenities}
                priceRange={priceRange}
                availability={{ hasDateRange: !!(searchFilters?.startDate && searchFilters?.endDate) }}
              />
            </div>
          </aside>

          {/* Boats Content */}
          <div className="flex-1 min-w-0" data-cid="catalog-results-col">
            {/* Banner combinazioni: appare se la richiesta passeggeri eccede la capacità max singola */}
            {(boatCombinations.length > 0 || combosLoading) && !loading && (
              <div className="mb-8 bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.12)]" data-cid="catalog-combinations-banner">
                <div className="mb-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-slate-950 text-white flex items-center justify-center font-bold text-base ring-1 ring-inset ring-white/10">2×</div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-display font-bold text-slate-900">
                      Combina 2 barche per {searchFilters?.passengers} persone
                    </h3>
                    <p className="text-sm text-slate-500">
                      Nessuna barca singola copre la richiesta. Queste coppie sì — prenotazione separata per ciascuna.
                    </p>
                  </div>
                </div>

                {combosLoading ? (
                  <p className="text-sm text-slate-500 py-8 text-center">Ricerca combinazioni…</p>
                ) : (
                  <div className="space-y-4">
                    {boatCombinations.slice(0, 6).map((combo: any, idx: number) => (
                      <div key={idx} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2">
                          {[combo.boatA, combo.boatB].map((b: any, i: number) => (
                            <a
                              key={i}
                              href={`/boat/${b.slug ?? b.id}`}
                              className={`flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors border-slate-100 ${i === 0 ? 'border-b md:border-b-0 md:border-r' : ''}`}
                            >
                              {b.image_url ? (
                                <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden shrink-0">
                                  <Image
                                    src={b.image_url}
                                    alt={b.name}
                                    fill
                                    sizes="112px"
                                    quality={75}
                                    loading="lazy"
                                    className="object-cover"
                                    unoptimized={b.image_url.startsWith('http')}
                                  />
                                </div>
                              ) : (
                                <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl bg-gradient-to-br from-sky-400 to-emerald-500 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-base font-black text-slate-900 truncate">{b.name}</p>
                                <p className="text-sm text-slate-500 truncate mt-0.5">{b.port_name || b.location}</p>
                                <div className="flex items-center gap-3 mt-2 flex-wrap">
                                  <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md tabular-nums">
                                    {b.capacity} posti
                                  </span>
                                  <span className="text-sm font-bold text-slate-900">
                                    €{b.price_per_day}<span className="text-xs text-slate-500 font-medium">/gg</span>
                                  </span>
                                </div>
                                {/* Anti-disintermediazione: il nome del partner NON è esposto al
                                    cliente nelle card pubbliche per evitare contatti diretti
                                    fuori piattaforma. */}
                              </div>
                            </a>
                          ))}
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-black text-slate-900">
                              {combo.boatA.capacity} + {combo.boatB.capacity} = {combo.combinedCapacity} posti
                            </span>
                            {combo.sameOwner && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700">
                                Stesso vendor
                              </span>
                            )}
                            {combo.samePort && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-sky-100 text-sky-700">
                                Stesso porto
                              </span>
                            )}
                          </div>
                          <div className="text-base font-black text-slate-900">
                            €{combo.combinedPrice.toFixed(2)}<span className="text-xs font-bold text-slate-500">/gg</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-32" data-cid="catalog-loading">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">{t('home.loadingBoats')}</p>
                </div>
              </div>
            ) : filteredAndSortedBoats.length === 0 && boatCombinations.length === 0 && !combosLoading ? (
              <div className="text-center py-32 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200 animate-fade-in" data-cid="catalog-empty">
                <div className="max-w-md mx-auto space-y-4">
                  <p className="text-slate-400 text-lg font-medium">{t('home.noResults')}</p>
                  <p className="text-sm text-slate-400">{t('home.noResultsHint')}</p>
                  <button
                    onClick={handleResetAll}
                    className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    {t('home.resetFiltersBtn')}
                  </button>
                </div>
              </div>
            ) : filteredAndSortedBoats.length === 0 ? (
              null
            ) : viewMode === 'map' ? (
              <BoatMap boats={filteredAndSortedBoats} className="h-[600px]" />
            ) : viewMode === 'split' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-180px)] min-h-[600px]" data-cid="catalog-split-view">
                <div className="overflow-y-auto pr-2 space-y-3">
                  {paginatedBoats.map((boat) => (
                    <div
                      key={boat.id}
                      onMouseEnter={() => setHoveredBoatId(boat.id)}
                      onMouseLeave={() => setHoveredBoatId((cur) => cur === boat.id ? null : cur)}
                    >
                      <BoatListCard
                        boat={boat}
                        searchFilters={searchFilters}
                        isFavorite={isFavorite(boat.id)}
                        onToggleFavorite={toggleFavorite}
                        isUnavailable={searchFilters?.startDate ? availabilityMap[boat.id] === false : false}
                        onClickUnavailable={handleClickUnavailable}
                      />
                    </div>
                  ))}
                </div>
                <div className="lg:sticky lg:top-24 h-full">
                  <BoatMap
                    boats={filteredAndSortedBoats}
                    className="h-full"
                    activeBoatId={hoveredBoatId}
                  />
                </div>
              </div>
            ) : viewMode === 'list' ? (
              <div className="space-y-4" data-cid="catalog-list-view">
                {paginatedBoats.map((boat) => (
                  <BoatListCard
                    key={boat.id}
                    boat={boat}
                    searchFilters={searchFilters}
                    isFavorite={isFavorite(boat.id)}
                    onToggleFavorite={toggleFavorite}
                    isUnavailable={searchFilters?.startDate ? availabilityMap[boat.id] === false : false}
                    onClickUnavailable={handleClickUnavailable}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-cid="catalog-grid-view">
                {paginatedBoats.map((boat) => (
                  <BoatCard
                    key={boat.id}
                    boat={boat}
                    searchFilters={searchFilters}
                    isFavorite={isFavorite(boat.id)}
                    onToggleFavorite={toggleFavorite}
                    isUnavailable={searchFilters?.startDate ? availabilityMap[boat.id] === false : false}
                    onClickUnavailable={handleClickUnavailable}
                  />
                ))}
              </div>
            )}


            {/* Pagination */}
            {filteredAndSortedBoats.length > 0 && viewMode !== 'map' && (
              <div className="mt-12" data-cid="catalog-pagination-wrap">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={filteredAndSortedBoats.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>
            )}
          </div>
        </div>
        </div>
      </section>
      )}

      {/* Mobile Filters Modal */}
      <CatalogFilters
        filters={catalogFilters}
        onFiltersChange={setCatalogFilters}
        availableCities={availableCities}
        availablePorts={availablePorts}
        priceRange={priceRange}
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        isMobile={true}
        availability={{ hasDateRange: !!(searchFilters?.startDate && searchFilters?.endDate) }}
      />

      {/* Unavailable Boat Modal */}
      {unavailableModalBoat && searchFilters && (
        <UnavailableBoatModal
          isOpen={true}
          onClose={() => setUnavailableModalBoat(null)}
          boat={unavailableModalBoat}
          startDate={searchFilters.startDate}
          endDate={searchFilters.endDate}
          similarBoats={similarAvailableBoats}
          onSelectDates={handleSelectAlternativeDates}
        />
      )}

      {!hasActiveFilters && (
        <section className="py-12 bg-sky-100/50 border-y border-sky-200/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col items-center text-center space-y-8">
              <div className="w-px h-16 bg-gradient-to-b from-blue-600 to-transparent" />
              <h2 data-cid="home-destinations-title" className="text-2xl font-serif font-bold text-blue-900">{t('home.exclusiveDestinations')}</h2>
              <p className="text-blue-800/60 max-w-xl">{t('home.exclusiveDestinationsDesc')}</p>
              <Link
                href="/destinations"
                className="group flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-blue-600"
              >
                {t('home.exploreMap')} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Trust bar premium prima del footer globale (solo gated user) */}
      <PremiumTrustBar />
    </div>
    </CompareProvider>
  );
}
